/*
 * Testimonia — mobile drawer motion
 *
 * Replaces the open/close choreography of the mobile drawer accordions with
 * GSAP tweens, makes them exclusive, and supplies the close branch the base
 * theme never had. GSAP is already loaded globally from layout/theme.liquid,
 * so this adds no new dependency.
 *
 * The desktop mega menu is deliberately left alone: burger-menu.js keeps its
 * own behaviour.
 *
 * Nothing here edits a base theme file. MenuDrawer cannot be patched on the
 * prototype because it binds its summary handler with .bind(this), which
 * captures the method reference at bind time. A capture-phase listener on the
 * drawer runs before that bound handler and stops it, which lets us own nested
 * summaries without touching global.js.
 *
 * Loaded by sections/testimonia-fonts-loader.liquid.
 */
(function () {
  'use strict';

  var REVEAL_EASE = 'back.out(0.8)';
  var DRAWER_OPEN_DURATION = 0.42;
  var DRAWER_CLOSE_DURATION = 0.28;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function animated() {
    return typeof window.gsap !== 'undefined' && !reducedMotion.matches;
  }

  /* Reads the height the element would take with no inline height applied.
     Forces a synchronous layout, so call it once per interaction. */
  function naturalHeight(el) {
    var previous = el.style.height;
    el.style.height = 'auto';
    var height = el.getBoundingClientRect().height;
    el.style.height = previous;
    return height;
  }

  /* GSAP runs its ticker off requestAnimationFrame, which the browser pauses
     whenever the tab stops rendering. A frozen reveal would strand the panel at
     height 0 — an invisible menu — and a frozen collapse would leave a painted
     panel that never tears down. Every tween is therefore paired with a timer
     that applies the same end state if the ticker never advanced. The margin is
     wide enough that it only ever wins when the tween genuinely stalled. */
  function settleAfter(tween, target, seconds, done) {
    var settled = false;

    /* A superseded timer has to die with the tween it belonged to. Open a panel
       and close it again inside a second and the reveal's timer would otherwise
       still be pending: it would kill the collapse tween mid-flight and snap the
       panel back to full height. The tween it replaces is already killed by the
       caller, so only the timer needs clearing. */
    if (target.testimoniaSettleTimer) {
      window.clearTimeout(target.testimoniaSettleTimer);
      target.testimoniaSettleTimer = null;
    }

    function finish() {
      if (settled) return;
      settled = true;
      window.clearTimeout(target.testimoniaSettleTimer);
      target.testimoniaSettleTimer = null;
      window.gsap.killTweensOf(target);
      done();
    }

    tween.eventCallback('onComplete', finish);
    target.testimoniaSettleTimer = window.setTimeout(finish, seconds * 1000 + 400);
  }

  /* ------------------------------------------------------------------ *
   * Mobile drawer accordions
   * ------------------------------------------------------------------ */

  function submenuOf(details) {
    return details.querySelector(':scope > .menu-drawer__submenu');
  }

  function summaryOf(details) {
    return details.querySelector(':scope > summary');
  }

  function setExpanded(details, expanded) {
    var summary = summaryOf(details);
    if (summary) summary.setAttribute('aria-expanded', String(expanded));
  }

  function collapse(details) {
    var submenu = submenuOf(details);
    details.classList.remove('menu-opening');
    setExpanded(details, false);

    if (!submenu || !animated()) {
      details.removeAttribute('open');
      details.classList.remove('testimonia-collapsing');
      return;
    }

    details.classList.add('testimonia-collapsing');
    window.gsap.killTweensOf(submenu);
    settleAfter(
      window.gsap.to(submenu, {
        height: 0,
        duration: DRAWER_CLOSE_DURATION,
        ease: 'power2.in'
      }),
      submenu,
      DRAWER_CLOSE_DURATION,
      function () {
        submenu.style.height = '';
        details.removeAttribute('open');
        details.classList.remove('testimonia-collapsing');
      }
    );
  }

  function expand(details) {
    /* Exclusive accordion: only one section per level stays open, so the drawer
       cannot grow past the viewport and push the summary you need to tap to
       close it off screen. */
    var item = details.closest('li');
    var list = item && item.parentElement;
    if (list) {
      Array.prototype.forEach.call(list.children, function (sibling) {
        var other = sibling.querySelector(':scope > details');
        if (other && other !== details && other.hasAttribute('open')) {
          collapse(other);
        }
      });
    }

    details.setAttribute('open', '');
    details.classList.remove('testimonia-collapsing');
    details.classList.add('menu-opening');
    setExpanded(details, true);

    var submenu = submenuOf(details);
    if (!submenu || !animated()) return;

    window.gsap.killTweensOf(submenu);
    var target = naturalHeight(submenu);
    settleAfter(
      window.gsap.fromTo(
        submenu,
        { height: 0 },
        { height: target, duration: DRAWER_OPEN_DURATION, ease: REVEAL_EASE }
      ),
      submenu,
      DRAWER_OPEN_DURATION,
      function () {
        submenu.style.height = '';
      }
    );
  }

  function initDrawer() {
    var drawer = document.querySelector('header-drawer');
    if (!drawer) return;

    /* The first details is the hamburger toggle itself, which MenuDrawer owns
       and which opens the whole drawer. Only the nested ones are ours. */
    var hamburger = drawer.querySelector('details');

    drawer.addEventListener(
      'click',
      function (event) {
        var summary = event.target.closest('summary');
        if (!summary || !drawer.contains(summary)) return;

        var details = summary.parentElement;
        if (!details || details === hamburger) return;

        /* Level 1 titles render as a real <a> to the collection page. Leave
           navigation alone and let the rest of the row do the toggling. */
        if (event.target.closest('a[href]')) return;

        /* Capture phase runs before the handler MenuDrawer bound on the summary
           itself, so stopping here is what keeps its version from firing. The
           base has no close branch for nested details: it re-adds menu-opening
           and re-arms a focus trap aimed at a display: none button. */
        event.preventDefault();
        event.stopPropagation();

        if (
          details.hasAttribute('open') &&
          !details.classList.contains('testimonia-collapsing')
        ) {
          collapse(details);
        } else {
          expand(details);
        }
      },
      true
    );
  }

  function init() {
    /* The DOM is fully parsed by the time a deferred script runs, so the drawer
       element is already there. */
    initDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
